/**
 * Where a Farsi visitor came from, carried from the landing URL to the signup.
 *
 * The 27 Nov gate on this surface is a capture number, and there are two ways
 * in: the Maktabkhooneh catalogue (16,582 students, every course page carries a
 * link) and the `@persiajs` Telegram group (288 members, read 21 Aug 2026).
 * Untagged, those two arrive as the same row, and "nobody wants this" and "one
 * route is broken" are opposite findings that lead to opposite decisions.
 *
 * The tag has to be sticky rather than read at submit time. A reader lands on
 * `/?src=mk`, clicks into an article, comes back to `/#newsletter` and signs up,
 * and by then `location.search` is empty. So the first page of the session wins
 * and is remembered for the session.
 *
 * `sessionStorage`, not `localStorage`, on purpose: a returning reader six weeks
 * later is not still a Maktabkhooneh arrival, and a stale tag is worse than none
 * because it is indistinguishable from a working one.
 */

const KEY = "fa-src";

/**
 * Only these. An open field would let a shared link invent sources forever, and
 * a `source` column with a long tail of typos and junk is not readable.
 *
 * The cost of the allowlist is that a NEW channel is silently untagged until its
 * name is added here, which looks exactly like a channel that sent nobody. So:
 * **add the name here in the same change that starts using the link, never
 * after.** `virgool` was added on 22 Aug 2026 before a single post existed, for
 * precisely that reason.
 */
const KNOWN = new Set([
  "mk", // Maktabkhooneh, the catalogue: 8 course pages plus the teacher page
  "telegram",
  "tg", // hand-typed shorthand, normalised to "telegram" below
  "virgool", // virgool.io, Persian blogging platform
  "linkedin",
  "yt",
  "email",
]);

/** `telegram` and `tg` are the same route; the short one is for hand-typed links. */
function canonical(raw: string): string | null {
  const v = raw.trim().toLowerCase().slice(0, 20);
  if (!KNOWN.has(v)) return null;
  return v === "tg" ? "telegram" : v;
}

/**
 * Read `?src=` off the current URL and remember it for the session.
 *
 * Called once on hydration, before anything can navigate away. Safe on the
 * server, where it does nothing and returns null.
 */
export function captureSource(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const fromUrl = canonical(new URL(window.location.href).searchParams.get("src") ?? "");
    if (fromUrl) {
      window.sessionStorage.setItem(KEY, fromUrl);
      return fromUrl;
    }
    return window.sessionStorage.getItem(KEY);
  } catch {
    // Private browsing modes throw on sessionStorage. An untagged signup is a
    // worse reading, never a failed one.
    return null;
  }
}

/** The remembered source, or null. */
export function currentSource(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}
